import { useState, useEffect, useCallback } from 'react'
import { CrewCheckIn, CheckInInput, AidStation } from '../types'
import { CrewStorage } from '../lib/crewStorage'
import { CrewCalculations } from '../lib/crewCalculations'

interface UseCrewCheckInsResult {
  checkIns: CrewCheckIn[]
  isLoading: boolean
  error: string | null
  addCheckIn: (aidStationId: string, input: CheckInInput) => Promise<boolean>
  removeCheckIn: (aidStationId: string) => void
  clearAllCheckIns: () => void
  hasCheckIn: (aidStationId: string) => boolean
  getCheckIn: (aidStationId: string) => CrewCheckIn | undefined
  shareUrl: string
  importFromUrl: (url: string) => Promise<boolean>
  refresh: () => void
}

export function useCrewCheckIns(
  slug: string,
  aidStations: AidStation[] = [],
  startTime?: Date
): UseCrewCheckInsResult {
  const [checkIns, setCheckIns] = useState<CrewCheckIn[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Load check-ins from storage
  const loadCheckIns = useCallback(() => {
    try {
      setIsLoading(true)
      setError(null)
      
      // First load from localStorage
      const stored = CrewStorage.getCheckIns(slug)
      
      // Then try to load from URL fragment
      const hasImported = CrewStorage.loadFromCurrentUrl(slug)
      
      // If we imported from URL, reload from storage
      const final = hasImported ? CrewStorage.getCheckIns(slug) : stored
      
      setCheckIns(final)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load check-ins')
    } finally {
      setIsLoading(false)
    }
  }, [slug])

  // Load check-ins on mount and slug change
  useEffect(() => {
    loadCheckIns()
  }, [loadCheckIns])

  // Listen for localStorage changes to sync across components/tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === `crew_checkins_${slug}` || e.key === null) {
        // Reload check-ins when they change in localStorage
        loadCheckIns()
      }
    }

    // Listen for storage events (changes from other tabs/windows)
    window.addEventListener('storage', handleStorageChange)
    
    // Also listen for custom events (changes from same tab)
    const handleCustomEvent = () => {
      loadCheckIns()
    }
    window.addEventListener(`crew_checkins_updated_${slug}`, handleCustomEvent)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener(`crew_checkins_updated_${slug}`, handleCustomEvent)
    }
  }, [slug, loadCheckIns])

  // Add a new check-in
  const addCheckIn = useCallback(async (
    aidStationId: string,
    input: CheckInInput
  ): Promise<boolean> => {
    try {
      setError(null)
      
      // Find the aid station
      const aidStation = aidStations.find(s => s.id === aidStationId)
      if (!aidStation) {
        setError('Aid station not found')
        return false
      }

      let actualTimeMinutes: number
      const now = new Date()

      // Calculate actual time based on input method
      switch (input.method) {
        case 'now':
          if (!startTime) {
            setError('Cannot use "Now" without race start time')
            return false
          }
          actualTimeMinutes = (now.getTime() - startTime.getTime()) / (1000 * 60)
          break

        case 'clock':
          if (!input.clockTime) {
            setError('Clock time is required')
            return false
          }
          if (!startTime) {
            setError('Cannot use clock time without race start time')
            return false
          }
          const elapsedFromClock = CrewCalculations.convertClockTimeToElapsed(
            input.clockTime,
            startTime
          )
          if (elapsedFromClock === null) {
            setError('Invalid clock time format. Use HH:MM')
            return false
          }
          actualTimeMinutes = elapsedFromClock
          break

        case 'elapsed':
          if (!input.elapsedTime) {
            setError('Elapsed time is required')
            return false
          }
          const parsed = CrewCalculations.parseTimeInput(input.elapsedTime, 'elapsed')
          if (parsed === null) {
            setError('Invalid elapsed time format. Use MM:SS or HH:MM:SS')
            return false
          }
          actualTimeMinutes = parsed
          break

        default:
          setError('Invalid check-in method')
          return false
      }

      // Create the check-in object
      const checkIn: CrewCheckIn = {
        aidStationId,
        actualTimeMinutes,
        method: input.method,
        timestamp: now,
        notes: input.notes
      }

      // Validate the check-in
      const validationError = CrewStorage.validateCheckIn(checkIn)
      if (validationError) {
        setError(validationError)
        return false
      }

      // Save to storage
      CrewStorage.addCheckIn(slug, checkIn)
      
      // Update local state
      const updatedCheckIns = CrewStorage.getCheckIns(slug)
      setCheckIns(updatedCheckIns)
      
      return true
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add check-in'
      setError(message)
      return false
    }
  }, [slug, aidStations, startTime])

  // Remove a check-in
  const removeCheckIn = useCallback((aidStationId: string) => {
    try {
      setError(null)
      CrewStorage.removeCheckIn(slug, aidStationId)
      const updatedCheckIns = CrewStorage.getCheckIns(slug)
      setCheckIns(updatedCheckIns)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove check-in')
    }
  }, [slug])

  // Clear all check-ins
  const clearAllCheckIns = useCallback(() => {
    try {
      setError(null)
      CrewStorage.clearCheckIns(slug)
      setCheckIns([])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear check-ins')
    }
  }, [slug])

  // Check if an aid station has a check-in
  const hasCheckIn = useCallback((aidStationId: string): boolean => {
    return checkIns.some(c => c.aidStationId === aidStationId)
  }, [checkIns])

  // Get check-in for an aid station
  const getCheckIn = useCallback((aidStationId: string): CrewCheckIn | undefined => {
    return checkIns.find(c => c.aidStationId === aidStationId)
  }, [checkIns])

  // Get shareable URL
  const shareUrl = CrewStorage.getShareableUrl(slug)

  // Import check-ins from URL
  const importFromUrl = useCallback(async (url: string): Promise<boolean> => {
    try {
      setError(null)
      const success = CrewStorage.importFromUrl(slug, url)
      
      if (success) {
        const updatedCheckIns = CrewStorage.getCheckIns(slug)
        setCheckIns(updatedCheckIns)
        return true
      } else {
        setError('No valid check-in data found in URL')
        return false
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import check-ins')
      return false
    }
  }, [slug])

  // Refresh data from storage
  const refresh = useCallback(() => {
    loadCheckIns()
  }, [loadCheckIns])

  return {
    checkIns,
    isLoading,
    error,
    addCheckIn,
    removeCheckIn,
    clearAllCheckIns,
    hasCheckIn,
    getCheckIn,
    shareUrl,
    importFromUrl,
    refresh
  }
}

// Hook for calculating ETA data with check-ins
export function useCrewETAs(
  aidStations: AidStation[],
  segments: any[], // Segment[] - avoiding import cycle
  calibrationProfile: any, // CalibrationProfile - avoiding import cycle
  checkIns: CrewCheckIn[]
) {
  const [etaData, setEtaData] = useState<Map<string, any>>(new Map())

  useEffect(() => {
    const calculations = CrewCalculations.calculateAllAidStationETAs(
      aidStations,
      segments,
      calibrationProfile,
      checkIns
    )
    setEtaData(calculations)
  }, [aidStations, segments, calibrationProfile, checkIns])

  return etaData
}